/**
 * Same idea as apps/guest-portal/src/lib/permissionInstructions.ts (no
 * shared package for it yet — small, deliberate duplicate, same as
 * geofence.ts). A denied browser permission can't be re-prompted by the
 * page itself once denied (that's a deliberate browser restriction) — the
 * only way out is the device/browser's own settings, and where that lives
 * differs enough by platform that a single generic sentence isn't much
 * help. Detected once from the UA string, not re-derived per render.
 *
 * Generalized over AppPermissionName rather than hardcoded to location —
 * POS only needs "geolocation" today, but a future requirement (say,
 * "camera" for a barcode scanner, or "notifications") only means adding
 * one entry to the union below plus a label/copy branch, not a new file.
 */
export type AppPermissionName = "geolocation" | "camera";

export type DevicePlatform = "ios" | "android" | "mac-safari" | "chrome" | "edge" | "firefox" | "other";

export function detectDevicePlatform(): DevicePlatform {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Edg\//.test(ua)) return "edge";
  if (/Chrome\//.test(ua) || /Chromium\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "mac-safari";
  return "other";
}

export interface PermissionInstructions {
  title: string;
  steps: string[];
}

const PERMISSION_LABEL: Record<AppPermissionName, string> = {
  geolocation: "Location",
  camera: "Camera",
};

const CHROME_CONTENT_PATH: Record<AppPermissionName, string> = {
  geolocation: "location",
  camera: "camera",
};

export function permissionInstructionsFor(permission: AppPermissionName, platform: DevicePlatform): PermissionInstructions {
  const label = PERMISSION_LABEL[permission];
  const iosServiceName = permission === "geolocation" ? "Location Services" : "Camera";
  switch (platform) {
    case "ios":
      return {
        title: `Turn on ${label} for this app`,
        steps: [
          "Open the Settings app",
          `Scroll down to Safari (or this app's own name, if it was added to your Home Screen) and tap ${label}`,
          "Choose \"Allow\"",
          `If ${label} is off for the whole device: Settings → Privacy & Security → ${iosServiceName} → turn it on`,
        ],
      };
    case "android":
      return {
        title: `Turn on ${label} for this app`,
        steps: [
          "Tap the lock or info icon in the browser's address bar",
          `Tap Permissions (or Site settings) → ${label} → Allow`,
          `If ${label} is off for the whole device: Settings → ${label} → turn it on`,
        ],
      };
    case "mac-safari":
      return {
        title: `Turn on ${label} for this site`,
        steps: [
          `Open Safari → Settings for This Website (or Safari menu → Settings → Websites → ${label})`,
          `Set ${label} to "Allow" for this site`,
          `If ${label} is off for the whole Mac: System Settings → Privacy & Security → ${iosServiceName} → turn it on`,
        ],
      };
    case "chrome":
      return {
        title: `Turn on ${label} for this site`,
        steps: [
          "Click the lock icon (or the tune icon) to the left of the address bar",
          `Set ${label} to "Allow"`,
          `If it's still blocked: chrome://settings/content/${CHROME_CONTENT_PATH[permission]} → remove this site from the blocked list`,
        ],
      };
    case "edge":
      return {
        title: `Turn on ${label} for this site`,
        steps: [
          "Click the lock icon to the left of the address bar",
          `Set ${label} to "Allow"`,
          `If it's still blocked: edge://settings/content/${CHROME_CONTENT_PATH[permission]} → remove this site from the blocked list`,
        ],
      };
    case "firefox":
      return {
        title: `Turn on ${label} for this site`,
        steps: [
          "Click the lock icon to the left of the address bar",
          `Clear the ${label} permission (or set it to Allow) and reload`,
        ],
      };
    default:
      return {
        title: `Turn on ${label} for this site`,
        steps: [`Open this browser's site settings and allow ${label} for this page, then come back and try again.`],
      };
  }
}
